import { CircleIcon as CircleSmall, User } from 'lucide-react'
import { assetPath } from '@/utils/assetPath'
import { Badge } from '@/components/shared/atoms/Badge'
import { useUserSummary } from '@/hooks/api/useUsers'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useCollection } from '@/hooks/api/useCollections'
import { Link } from '@tanstack/react-router'
import { transformMedium, ProductDto } from '@wtf/shared'
import { productsApi } from '@wtf/shared/api/products'
import { usePostHog } from 'posthog-js/react'
import { AnalyticsEvents, EventContext } from '@/utils/analytics'
import { useQueries, useQueryClient } from '@tanstack/react-query'

import { replaceImageDomain } from '@/utils/replaceImageDomain'

// Default cover images that trigger the product collage
const DEFAULT_COVER_URLS = [
    'https://r2.wtf.style/random%20colour-01.png',
    'https://r2.wtf.style/random%20colour-02.png',
    'https://r2.wtf.style/random%20colour-03.png',
    'https://r2.wtf.style/random%20colour-04.png',
    'https://r2.wtf.style/random%20colour-05.png',
    'https://r2.wtf.style/random%20colour-06.png',
    'https://r2.whatthefuckareyouwearing.com/random%20colour-01.png',
    'https://r2.whatthefuckareyouwearing.com/random%20colour-02.png',
    'https://r2.whatthefuckareyouwearing.com/random%20colour-03.png',
    'https://r2.whatthefuckareyouwearing.com/random%20colour-04.png',
    'https://r2.whatthefuckareyouwearing.com/random%20colour-05.png',
    'https://r2.whatthefuckareyouwearing.com/random%20colour-06.png',
]

/**
 * Check if the cover URL is a default cover (should show product collage instead)
 */
const isDefaultCover = (url: string | null | undefined): boolean =>
    !url || DEFAULT_COVER_URLS.some((d) => url.includes(d))

/**
 * Get the first image URL from a product
 */
const getProductImageUrl = (product: ProductDto | undefined): string | null => {
    if (!product?.images?.length) return null
    const url = transformMedium(product.images[0].url) || product.images[0].url
    return replaceImageDomain(url)
}

interface ProductCollageProps {
    products: (ProductDto | undefined)[]
    priority?: boolean
    altText: string
}

/**
 * Renders a collage of product images with adaptive layout:
 * - 1 product: full cover
 * - 2 products: 50/50 horizontal split
 * - 3+ products: 1 left (50%), 2 stacked right (50% each)
 */
function ProductCollage({ products, priority, altText }: ProductCollageProps) {
    const validProducts = products.filter(
        (p): p is ProductDto => !!p && !!getProductImageUrl(p)
    )
    const imageUrls = validProducts
        .slice(0, 3)
        .map((p) => getProductImageUrl(p)!)

    if (imageUrls.length === 0) {
        // Fallback to placeholder
        return (
            <img
                src="https://placehold.co/600x400?text=No+Products"
                alt={altText}
                className="h-full w-full object-cover"
                loading={priority ? 'eager' : 'lazy'}
            />
        )
    }

    if (imageUrls.length === 1) {
        // Single product - full cover
        return (
            <img
                src={imageUrls[0]}
                alt={altText}
                className="h-full w-full object-cover"
                loading={priority ? 'eager' : 'lazy'}
            />
        )
    }

    if (imageUrls.length === 2) {
        // Two products - 50/50 horizontal split
        return (
            <div className="flex h-full w-full flex-row gap-px bg-gray-300">
                <div className="relative w-1/2">
                    <img
                        src={imageUrls[0]}
                        alt={`${altText} - Product 1`}
                        className="absolute inset-0 h-full w-full object-cover"
                        loading={priority ? 'eager' : 'lazy'}
                    />
                </div>
                <div className="relative w-1/2">
                    <img
                        src={imageUrls[1]}
                        alt={`${altText} - Product 2`}
                        className="absolute inset-0 h-full w-full object-cover"
                        loading={priority ? 'eager' : 'lazy'}
                    />
                </div>
            </div>
        )
    }

    // 3+ products - 1 left (50%), 2 stacked right
    return (
        <div className="flex h-full w-full flex-row gap-px bg-gray-300">
            <div className="relative w-1/2">
                <img
                    src={imageUrls[0]}
                    alt={`${altText} - Product 1`}
                    className="absolute inset-0 h-full w-full object-cover"
                    loading={priority ? 'eager' : 'lazy'}
                />
            </div>
            <div className="flex w-1/2 flex-col gap-px">
                <div className="relative flex-1">
                    <img
                        src={imageUrls[1]}
                        alt={`${altText} - Product 2`}
                        className="absolute inset-0 h-full w-full object-cover"
                        loading={priority ? 'eager' : 'lazy'}
                    />
                </div>
                <div className="relative flex-1">
                    <img
                        src={imageUrls[2]}
                        alt={`${altText} - Product 3`}
                        className="absolute inset-0 h-full w-full object-cover"
                        loading={priority ? 'eager' : 'lazy'}
                    />
                </div>
            </div>
        </div>
    )
}

interface CollectionCardProps {
    collectionId: string
    variant?: 'default' | 'featured'
    showAvatar?: boolean
    priority?: boolean
    context?: EventContext // Context for CTR tracking
}

export function CollectionCard({
    collectionId,
    variant = 'default',
    showAvatar = true,
    priority = false,
    context,
}: CollectionCardProps) {
    const queryClient = useQueryClient()
    const { data: collection, isLoading: collectionLoading } =
        useCollection(collectionId)

    // Fetch owner summary for avatar (only if collection is loaded)
    const { data: owner, isLoading: ownerLoading } = useUserSummary(
        collection?.ownerId
    )

    // Fetch current user to determine ownership
    const { user: currentUser } = useCurrentUser()
    const posthog = usePostHog()

    // Determine if we should show the product collage (default cover with products)
    const shouldShowCollage =
        !!collection &&
        isDefaultCover(collection.coverImageUrl) &&
        (collection.productIds?.length ?? 0) > 0

    // Get first 3 product IDs for collage
    const productIdsForCollage = shouldShowCollage
        ? (collection?.productIds?.slice(0, 3) ?? [])
        : []

    // Fetch products for collage - uses cache if available, fetches if needed
    const productQueries = useQueries({
        queries: productIdsForCollage.map((productId) => ({
            queryKey: ['product', productId],
            queryFn: () => productsApi.getProductById(productId),
            enabled: shouldShowCollage && !!productId,
            staleTime: Infinity, // Never refetch - cover images don't need to be fresh
            initialData: () =>
                queryClient.getQueryData<ProductDto>(['product', productId]),
        })),
    })

    // Extract product data from queries
    const collageProducts = productQueries.map((query) => query.data)

    // Determine if the current user is the owner
    const isOwnedByCurrentUser =
        !!currentUser && !!collection && currentUser.uid === collection.ownerId

    const handleCollectionClick = () => {
        if (collection) {
            posthog?.capture(AnalyticsEvents.COLLECTION_CARD_CLICKED, {
                collection_id: collection.id,
                collection_name: collection.name,
                ...(context && { context }),
            })
        }
    }

    if (collectionLoading || !collection) {
        // Loading skeleton or fallback
        return (
            <div className="relative h-full max-h-[173px] animate-pulse overflow-hidden rounded-2xl bg-gray-100">
                <div className="relative aspect-4/3 w-full rounded-lg bg-gray-200" />
                <div className="absolute top-1 right-1 left-1 flex justify-between md:top-2 md:right-2 md:left-2">
                    <div className="flex items-center gap-1">
                        {variant === 'default' ? (
                            <>
                                <div className="h-6 w-12 rounded bg-gray-300" />
                                <div className="h-6 w-12 rounded bg-gray-300" />
                            </>
                        ) : null}
                    </div>
                    {/* Show avatar if featured variant, or if not owned by current user */}
                    {showAvatar &&
                        (variant === 'featured' || !isOwnedByCurrentUser) && (
                            <div className="h-7 w-7 rounded-full bg-gray-300 md:h-9 md:w-9" />
                        )}
                </div>
                {variant === 'featured' && (
                    <div className="absolute top-1 right-1 left-1 px-3 py-2 md:top-2 md:right-2 md:left-2">
                        <div className="h-6 w-24 rounded bg-gray-300" />
                    </div>
                )}
                <div className="absolute right-1 bottom-1 left-1 px-3 py-2 md:right-2 md:bottom-2 md:left-2">
                    <div className="h-4 w-24 rounded bg-gray-300" />
                </div>
            </div>
        )
    }

    // Use totalProductCount/totalLookCount for accurate counts (productIds may only contain preview IDs)
    const lookCount = collection.totalLookCount ?? 0
    const productCount = collection.totalProductCount ?? 0

    // Only compute custom cover URL if not using collage
    const customCoverUrl = !shouldShowCollage
        ? collection.coverImageUrl
            ? replaceImageDomain(
                transformMedium(collection.coverImageUrl) ||
                collection.coverImageUrl
            )
            : 'https://placehold.co/600x400?text=No+Image'
        : null

    return (
        <Link
            to="/collections/$id"
            params={{ id: collection.id }}
            onClick={handleCollectionClick}
            className="group relative block h-full cursor-pointer overflow-hidden rounded-2xl bg-gray-100"
            tabIndex={0}
        >
            <div className="relative aspect-4/3 w-full overflow-hidden rounded-lg bg-gray-200">
                {shouldShowCollage ? (
                    <ProductCollage
                        products={collageProducts}
                        priority={variant === 'default' && priority}
                        altText={collection.name}
                    />
                ) : (
                    <img
                        src={customCoverUrl!}
                        alt={collection.name}
                        className="h-full w-full object-cover"
                        loading={
                            variant === 'default' && priority ? 'eager' : 'lazy'
                        }
                    />
                )}
                {/* Hover overlay */}
                <div className="absolute inset-0 rounded-lg bg-gray-900/30 opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
            </div>
            <div className="absolute top-1 right-1 left-1 flex justify-between md:top-2 md:right-2 md:left-2">
                <div className="flex items-center gap-1">
                    {variant === 'default' && (
                        <>
                            {lookCount > 0 && (
                                <Badge
                                    className="py-[6px] md:py-2"
                                    icon={
                                        <img
                                            alt="looks"
                                            src={assetPath(
                                                'icons/circle-three.svg'
                                            )}
                                            width={16}
                                            height={16}
                                        />
                                    }
                                    label={lookCount}
                                />
                            )}
                            <Badge
                                className="py-[6px] md:py-2"
                                icon={
                                    <CircleSmall
                                        className="fill-white"
                                        size={14}
                                    />
                                }
                                label={`${productCount}`}
                            />
                        </>
                    )}
                </div>
                {/* Show avatar if featured variant, or if not owned by current user */}
                {showAvatar &&
                    (variant === 'featured' || !isOwnedByCurrentUser) && (
                        <div className="h-7 w-7 overflow-hidden rounded-full text-white md:h-9 md:w-9">
                            {ownerLoading ? (
                                <div className="h-full w-full animate-pulse rounded-full bg-gray-300" />
                            ) : owner?.avatarUrl ? (
                                <img
                                    alt={
                                        owner?.name ||
                                        owner?.username ||
                                        'Owner avatar'
                                    }
                                    src={owner.avatarUrl}
                                    width={80}
                                    height={80}
                                    className="h-full w-full rounded-full object-cover"
                                    loading={
                                        variant === 'default' && priority
                                            ? 'eager'
                                            : 'lazy'
                                    }
                                />
                            ) : (
                                <div className="flex h-7 w-7 items-center justify-center rounded-full border border-white bg-gray-100 md:h-9 md:w-9">
                                    <User className="h-4 w-4 text-gray-600 md:h-5 md:w-5" />
                                </div>
                            )}
                        </div>
                    )}
            </div>
            {/* Username for featured variant - positioned to match collection name styling */}
            {variant === 'featured' && (
                <p
                    className="absolute top-1 right-1 left-1 flex bg-transparent px-3 py-2 text-sm text-white md:top-2 md:right-2 md:left-2"
                    style={{ textShadow: '0px 0px 10px #000000' }}
                >
                    {ownerLoading ? (
                        <span className="inline-block h-6 w-24 animate-pulse rounded bg-gray-300" />
                    ) : (
                        owner?.username
                    )}
                </p>
            )}
            <p
                className="absolute right-1 bottom-1 left-1 flex bg-transparent px-3 py-2 text-sm text-white md:right-2 md:bottom-2 md:left-2"
                style={{ textShadow: '0px 0px 20px #000000' }}
            >
                {collection.name}
            </p>
        </Link>
    )
}
